import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { LayoutDashboard, Settings, BarChart3, LogOut, Tv, Key, Mail, ExternalLink, User, Camera, X, Eye, EyeOff, Sun, Moon, Users } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import FloatingWidget from './FloatingWidget';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function Sidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, user, updateUser } = useAuth();
  const { darkMode, toggleDarkMode } = useTheme();
  const [unreadCount, setUnreadCount] = useState(0);
  const [widgetOpen, setWidgetOpen] = useState(() => {
    return localStorage.getItem('widgetOpen') === 'true';
  });
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [profilePicture, setProfilePicture] = useState(null);
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef(null);

  // Initialize form with user data
  useEffect(() => {
    if (user) {
      setFirstName(user.first_name || '');
      setLastName(user.last_name || '');
      setProfilePicture(user.profile_picture || null);
    }
  }, [user]);

  // Save widget state to localStorage
  useEffect(() => {
    localStorage.setItem('widgetOpen', widgetOpen.toString());
  }, [widgetOpen]);

  // Poll for unread message count
  useEffect(() => {
    const fetchUnreadCount = async () => {
      try {
        const res = await axios.get(API + '/messages/unread-count');
        setUnreadCount(res.data.unread_count || 0);
      } catch (error) {
        // Silently fail
      }
    };
    
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 5000);
    return () => clearInterval(interval);
  }, []);

  const navItems = [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard', testId: 'nav-dashboard' },
    { icon: Mail, label: 'Messages', path: '/messaging', testId: 'nav-messaging', badge: unreadCount },
    { icon: BarChart3, label: 'Reports', path: '/reports', testId: 'nav-reports' },
    { icon: Tv, label: 'Wallboard', path: '/wallboard', testId: 'nav-wallboard' },
  ];

  // Only show admin/license for admin users
  if (user?.role === 'admin') {
    navItems.push({ icon: Settings, label: 'Admin', path: '/admin', testId: 'nav-admin' });
    navItems.push({ icon: Key, label: 'License', path: '/license', testId: 'nav-license' });
  }

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      toast.error('Image must be less than 2MB');
      return;
    }

    const reader = new FileReader();
    reader.onload = async (event) => {
      const base64 = event.target?.result;
      setProfilePicture(base64);
    };
    reader.readAsDataURL(file);
  };

  const handleRemovePhoto = () => {
    setProfilePicture(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSaveSettings = async () => {
    // Validate password fields if attempting to change password
    if (newPassword || confirmPassword) {
      if (!currentPassword) {
        toast.error('Current password is required to change password');
        return;
      }
      if (newPassword !== confirmPassword) {
        toast.error('New passwords do not match');
        return;
      }
      if (newPassword.length < 6) {
        toast.error('New password must be at least 6 characters');
        return;
      }
    }

    setSaving(true);
    try {
      // Update profile info
      const profileRes = await axios.put(`${API}/users/${user.id}/profile`, {
        first_name: firstName,
        last_name: lastName,
        profile_picture: profilePicture
      });

      // Update password if provided
      if (newPassword && currentPassword) {
        await axios.put(`${API}/users/${user.id}/password`, {
          current_password: currentPassword,
          new_password: newPassword
        });
        toast.success('Password updated successfully');
      }

      // Update local user state
      if (updateUser) {
        updateUser({
          ...user,
          first_name: firstName,
          last_name: lastName,
          profile_picture: profilePicture
        });
      }

      toast.success('Settings saved successfully');
      setSettingsOpen(false);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      {/* Floating Widget */}
      <FloatingWidget isOpen={widgetOpen} onClose={() => setWidgetOpen(false)} />
      
      {/* User Settings Modal */}
      {settingsOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setSettingsOpen(false)}>
          <div 
            className="bg-white dark:bg-slate-800 rounded-2xl p-6 w-full max-w-md mx-4 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-slate-800 dark:text-white">User Settings</h2>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setSettingsOpen(false)}
                className="rounded-full"
              >
                <X className="w-5 h-5" />
              </Button>
            </div>

            {/* Profile Photo */}
            <div className="flex flex-col items-center mb-6">
              <div className="relative group">
                <div className="w-24 h-24 rounded-full bg-slate-200 dark:bg-slate-700 flex items-center justify-center overflow-hidden border-4 border-white dark:border-slate-600 shadow-lg">
                  {profilePicture ? (
                    <img src={profilePicture} alt="Profile" className="w-full h-full object-cover" />
                  ) : (
                    <User className="w-12 h-12 text-slate-400" />
                  )}
                </div>
                <div className="absolute inset-0 flex items-center justify-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="w-8 h-8 rounded-full bg-dp-600 text-white flex items-center justify-center hover:bg-dp-700 transition-colors"
                  >
                    <Camera className="w-4 h-4" />
                  </button>
                  {profilePicture && (
                    <button
                      onClick={handleRemovePhoto}
                      className="w-8 h-8 rounded-full bg-red-600 text-white flex items-center justify-center hover:bg-red-700 transition-colors"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handlePhotoUpload}
                className="hidden"
              />
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-2">Click to upload photo</p>
              {user?.id && (
                <p className="text-xs text-slate-400 dark:text-slate-500 mt-1 font-mono">
                  ID: {user.id}
                </p>
              )}
            </div>

            {/* Name Fields */}
            <div className="space-y-4 mb-6">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="firstName" className="text-sm text-slate-600 dark:text-slate-300">First Name</Label>
                  <Input
                    id="firstName"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    className="mt-1"
                    placeholder="First name"
                  />
                </div>
                <div>
                  <Label htmlFor="lastName" className="text-sm text-slate-600 dark:text-slate-300">Last Name</Label>
                  <Input
                    id="lastName"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    className="mt-1"
                    placeholder="Last name"
                  />
                </div>
              </div>
            </div>

            {/* Password Change Section */}
            <div className="border-t border-slate-200 dark:border-slate-700 pt-4 mb-6">
              <h3 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-3">Change Password</h3>
              <div className="space-y-3">
                <div className="relative">
                  <Label htmlFor="currentPassword" className="text-sm text-slate-600 dark:text-slate-300">Current Password</Label>
                  <div className="relative mt-1">
                    <Input
                      id="currentPassword"
                      type={showCurrentPassword ? 'text' : 'password'}
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      placeholder="Enter current password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <div className="relative">
                  <Label htmlFor="newPassword" className="text-sm text-slate-600 dark:text-slate-300">New Password</Label>
                  <div className="relative mt-1">
                    <Input
                      id="newPassword"
                      type={showNewPassword ? 'text' : 'password'}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="Enter new password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <div>
                  <Label htmlFor="confirmPassword" className="text-sm text-slate-600 dark:text-slate-300">Confirm New Password</Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="mt-1"
                    placeholder="Confirm new password"
                  />
                </div>
              </div>
            </div>

            {/* Save Button */}
            <Button
              onClick={handleSaveSettings}
              disabled={saving}
              className="w-full bg-dp-600 hover:bg-dp-700 text-white"
              data-testid="save-user-settings"
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </div>
      )}
      
      {/* Main Sidebar */}
      <div className="fixed left-4 top-1/2 -translate-y-1/2 flex flex-col items-center gap-3 z-40">
        {/* Vertical Navigation Bar */}
        <div className="h-auto w-20 rounded-full border border-white/30 glass-panel shadow-2xl flex flex-col items-center py-6 gap-4">
          <TooltipProvider>
            <div className="flex flex-col gap-4">
              {navItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                const hasUnread = item.badge > 0;
                
                return (
                  <Tooltip key={item.path}>
                    <TooltipTrigger asChild>
                      <Button
                        onClick={() => navigate(item.path)}
                        data-testid={item.testId}
                        variant="ghost"
                        size="icon"
                        className={`w-12 h-12 rounded-full transition-all relative ${
                          isActive 
                            ? 'bg-dp-600 text-white hover:bg-dp-700' 
                            : hasUnread
                              ? 'bg-red-500 text-white hover:bg-red-600'
                              : 'text-slate-600 dark:text-slate-300 hover:bg-white/50 dark:hover:bg-slate-700/50'
                        }`}
                      >
                        <Icon className="w-6 h-6" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="right">
                      <p>{item.label}{hasUnread ? ` (${item.badge} unread)` : ''}</p>
                    </TooltipContent>
                  </Tooltip>
                );
              })}
            </div>

            <div className="mt-4 pt-4 border-t border-white/20 flex flex-col gap-2">
              {/* Widget Toggle Button - hidden for front_office and office_manager */}
              {!(['front_office', 'office_manager'].includes(user?.role) || 
                 ['front_office', 'office_manager'].includes(user?.base_role)) && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    onClick={() => setWidgetOpen(!widgetOpen)}
                    data-testid="widget-toggle"
                    variant="ghost"
                    size="icon"
                    className={`w-12 h-12 rounded-full transition-all ${
                      widgetOpen 
                        ? 'bg-dp-600 text-white hover:bg-dp-700' 
                        : 'text-slate-600 dark:text-slate-300 hover:bg-white/50 dark:hover:bg-slate-700/50'
                    }`}
                  >
                    <ExternalLink className="w-6 h-6" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="right">
                  <p>{widgetOpen ? 'Hide Widget' : 'Show Widget'}</p>
                </TooltipContent>
              </Tooltip>
              )}

              {/* Switch Providers Button - assistants only */}
              {(user?.role === 'assistant' || user?.base_role === 'assistant') && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    onClick={() => {
                      sessionStorage.removeItem('assistant_selected_providers');
                      window.location.reload();
                    }}
                    data-testid="switch-providers-button"
                    variant="ghost"
                    size="icon"
                    className="w-12 h-12 rounded-full transition-all text-slate-600 dark:text-slate-300 hover:bg-white/50 dark:hover:bg-slate-700/50"
                  >
                    <Users className="w-6 h-6" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="right">
                  <p>Switch Providers</p>
                </TooltipContent>
              </Tooltip>
              )}

              {/* User Settings Button */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    onClick={() => setSettingsOpen(true)}
                    data-testid="user-settings-button"
                    variant="ghost"
                    size="icon"
                    className={`w-12 h-12 rounded-full transition-all ${
                      settingsOpen 
                        ? 'bg-dp-600 text-white hover:bg-dp-700' 
                        : 'text-slate-600 dark:text-slate-300 hover:bg-white/50 dark:hover:bg-slate-700/50'
                    }`}
                  >
                    {user?.profile_picture ? (
                      <img src={user.profile_picture} alt="Profile" className="w-8 h-8 rounded-full object-cover" />
                    ) : (
                      <User className="w-6 h-6" />
                    )}
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="right">
                  <p>User Settings</p>
                </TooltipContent>
              </Tooltip>

              {/* Logout Button */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    onClick={logout}
                    data-testid="logout-button"
                    variant="ghost"
                    size="icon"
                    className="w-12 h-12 rounded-full text-red-600 hover:bg-red-100/50 dark:hover:bg-red-900/30"
                  >
                    <LogOut className="w-6 h-6" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="right">
                  <p>Logout</p>
                </TooltipContent>
              </Tooltip>
            </div>
          </TooltipProvider>
        </div>

        {/* Dark Mode Toggle - Outside the bar, smaller and compact */}
        <div className="flex items-center gap-1.5 px-2 py-1.5 rounded-full bg-white/60 dark:bg-slate-800/60 border border-white/30 dark:border-slate-700/50 backdrop-blur-sm shadow-md">
          <Sun className={`w-3.5 h-3.5 transition-colors ${darkMode ? 'text-slate-400' : 'text-slate-600'}`} />
          <Switch
            checked={darkMode}
            onCheckedChange={toggleDarkMode}
            data-testid="dark-mode-toggle"
            className="data-[state=checked]:bg-slate-700 data-[state=unchecked]:bg-amber-400 scale-75"
          />
          <Moon className={`w-3.5 h-3.5 transition-colors ${darkMode ? 'text-slate-700' : 'text-slate-400'}`} />
        </div>
      </div>
    </>
  );
}
